// IANA timezone options for the screen timezone select. Prefer the platform's
// full list; fall back to a common subset on older engines.
const FALLBACK = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Phoenix',
  'America/Toronto',
  'America/Mexico_City',
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

export function timezoneOptions(): string[] {
  const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  try {
    if (typeof anyIntl.supportedValuesOf === 'function') {
      return anyIntl.supportedValuesOf('timeZone');
    }
  } catch {
    /* fall through */
  }
  return FALLBACK;
}

export function guessTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

export interface ZonedNow {
  weekday: number; // 0=Mon … 6=Sun (matches the schedule days_mask bit order)
  minutes: number; // minutes since local midnight, 0..1439
}

/**
 * Wall-clock weekday + minute-of-day for an instant in a given IANA timezone.
 * Used by the week calendar to place the "now" line and highlight today in the
 * screen's timezone (schedule times are stored as wall-clock in that zone).
 */
export function zonedNow(tz: string, atMs: number = Date.now()): ZonedNow {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date(atMs));
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    let hour = Number(get('hour'));
    if (hour === 24) hour = 0; // some engines emit 24 for midnight
    return {
      weekday: WEEKDAY_INDEX[get('weekday')] ?? 0,
      minutes: hour * 60 + Number(get('minute')),
    };
  } catch {
    return { weekday: 0, minutes: 0 };
  }
}
